import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { katexStyles, renderMarkdown, renderMarkdownInline } from '../services/markdown.js';
import { SseClient } from '../services/sse-client.js';
import type { ChatMessage, SessionMeta, SummaryResponse } from '../types.js';

// Famílias de fonte do tema do chat (chave do tema → font-family CSS). Espelha o
// FONT_MAP do painel pra o white-label ficar consistente entre chat e dashboard.
const CHAT_FONT_MAP: Record<string, string> = {
  system: 'system-ui, -apple-system, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: 'ui-monospace, "Courier New", monospace',
  condensed: '"Arial Narrow", "Roboto Condensed", system-ui, sans-serif',
};

// Anexo escolhido pelo aluno, ainda NÃO enviado (staged no cliente). Vira upload
// no envio da mensagem (POST /attachments).
interface StagedAttachment {
  localId: string;
  file: File;
  kind: 'image' | 'document';
  // objectURL pro thumbnail (só imagens).
  previewUrl?: string;
}

@customElement('sos-chat')
export class SosChat extends LitElement {
  static styles = [
    // KaTeX CSS injetado no Shadow DOM (display block precisa de scope)
    unsafeCSS(katexStyles),
    css`
    :host {
      display: flex;
      flex-direction: column;
      /* position:relative — ancora o overlay de bloqueio (.window-lock). */
      position: relative;
      /* White-label do chat: as vars --sos-chat-* vêm do tema da instituição
         (applyChatTheme no host); sem tema, caem no fallback (visual original). */
      font-family: var(--sos-chat-font, system-ui, -apple-system, sans-serif);
      color: var(--sos-chat-text, #1f1f1f);
      background: var(--sos-chat-bg, #fff);
      border-radius: var(--sos-chat-radius, 12px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      max-width: 720px;
      width: 100%;
      height: 600px;
    }
    /* Tela cheia via Fullscreen API nativa: o elemento vai pro "top layer",
       escapando qualquer ancestral com transform/contain que prenderia um
       position:fixed dentro da coluna do Moodle (causa do "não pegou a tela
       toda"). A UA já dimensiona pro full screen; só tiramos o teto de largura
       e os cantos/sombra. */
    :host(:fullscreen) {
      max-width: none;
      width: 100%;
      height: 100%;
      border-radius: 0;
      box-shadow: none;
    }
    :host(:fullscreen) .header {
      border-radius: 0;
    }
    /* Overlay que bloqueia o widget embutido enquanto a janela popup está
       aberta. Cobre tudo (inclui o header) — o componente fica "desabilitado"
       até a janela extra fechar. */
    .window-lock {
      position: absolute;
      inset: 0;
      z-index: 60;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: rgba(255, 255, 255, 0.94);
      border-radius: inherit;
    }
    .window-lock .wl-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      text-align: center;
      max-width: 280px;
    }
    .window-lock .wl-msg {
      color: #555;
      font-size: 14px;
      line-height: 1.4;
    }
    .window-lock .wl-actions {
      display: flex;
      gap: 8px;
    }
    .messages {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .bubble {
      max-width: 85%;
      padding: 10px 14px;
      border-radius: 14px;
      line-height: 1.5;
    }
    .bubble.user {
      align-self: flex-end;
      background: var(--sos-chat-accent, #1f1f1f);
      color: var(--sos-chat-accent-fg, #fff);
      white-space: pre-wrap;
    }
    .bubble.assistant {
      align-self: flex-start;
      background: var(--sos-chat-bubble, #f0f0f0);
      color: var(--sos-chat-text, #1f1f1f);
    }
    .bubble.blocked {
      align-self: flex-start;
      background: #fff3cd;
      color: #856404;
      border: 1px solid #ffeeba;
      white-space: pre-wrap;
    }
    .bubble.limit {
      align-self: stretch;
      background: #fdecea;
      color: #b71c1c;
      border: 1px solid #f5c6cb;
      font-weight: 600;
      white-space: pre-wrap;
    }
    /* Markdown body styles (apply to bubble.assistant que tem HTML render) */
    .bubble.assistant > *:first-child { margin-top: 0; }
    .bubble.assistant > *:last-child { margin-bottom: 0; }
    .bubble.assistant p { margin: 0.5em 0; }
    .bubble.assistant h1, .bubble.assistant h2, .bubble.assistant h3, .bubble.assistant h4 {
      font-weight: 700; line-height: 1.25; margin: 0.7em 0 0.3em;
    }
    .bubble.assistant h1 { font-size: 1.35em; }
    .bubble.assistant h2 { font-size: 1.2em; }
    .bubble.assistant h3 { font-size: 1.1em; }
    .bubble.assistant ul, .bubble.assistant ol {
      margin: 0.4em 0; padding-left: 1.5em;
    }
    .bubble.assistant li { margin: 0.15em 0; }
    .bubble.assistant strong { font-weight: 600; }
    .bubble.assistant em { font-style: italic; }
    .bubble.assistant code:not(pre code) {
      background: rgba(0,0,0,0.08); padding: 0.1em 0.35em; border-radius: 4px;
      font-size: 0.9em; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    }
    .bubble.assistant pre {
      background: #1f1f1f; color: #f5f5f5; padding: 0.7em 0.9em;
      border-radius: 6px; overflow-x: auto; font-size: 0.85em; margin: 0.5em 0;
    }
    .bubble.assistant pre code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      background: transparent; padding: 0; color: inherit;
    }
    .bubble.assistant a { color: #1f1f1f; text-decoration: underline; }
    .bubble.assistant table {
      border-collapse: collapse; font-size: 0.9em; margin: 0.5em 0; width: 100%;
    }
    .bubble.assistant th, .bubble.assistant td {
      border: 1px solid #ddd; padding: 4px 8px; text-align: left;
    }
    .bubble.assistant th { background: #fafafa; font-weight: 600; }
    .bubble.assistant blockquote {
      border-left: 3px solid #999; padding-left: 0.7em; color: #555; margin: 0.5em 0;
    }
    /* KaTeX display blocks centered + scrollable se largura passar */
    .bubble.assistant .katex-display {
      overflow-x: auto; overflow-y: hidden; padding: 0.3em 0;
    }
    /* Resposta do assistente + botão 🔊 (Ouvir/TTS) embaixo. O wrapper segura a
       largura da bolha e ancora o botão à esquerda. */
    .assistant-row {
      align-self: flex-start;
      display: flex;
      flex-direction: column;
      gap: 4px;
      max-width: 85%;
    }
    .assistant-row .bubble.assistant { max-width: 100%; }
    .tts-btn {
      align-self: flex-start;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 9px;
      background: transparent;
      border: 1px solid #ddd;
      border-radius: 999px;
      color: #555;
      font-size: 11.5px;
      cursor: pointer;
      font-family: inherit;
    }
    .tts-btn:hover:not(:disabled) { background: #f0f0f0; }
    .tts-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .tts-btn svg { width: 13px; height: 13px; display: block; }
    /* Balão de limite de uso — fica acima da barra de ações (gerar resumo /
       exportar PDF). Tom de alerta suave, fechável, reaparece a cada tentativa
       bloqueada. */
    .limit-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin: 8px 12px 0;
      padding: 8px 12px;
      background: #fff8e1;
      color: #8a6d3b;
      border: 1px solid #ffe0a3;
      border-radius: 8px;
      font-size: 12.5px;
      line-height: 1.4;
    }
    .limit-banner .lb-text strong {
      font-weight: 600;
    }
    .limit-banner .lb-close {
      flex: 0 0 auto;
      padding: 2px 6px;
      background: transparent;
      border: 0;
      color: inherit;
      font-size: 13px;
      line-height: 1;
      cursor: pointer;
      opacity: 0.65;
      border-radius: 4px;
    }
    .limit-banner .lb-close:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.06);
    }
    .actions {
      display: flex;
      gap: 8px;
      padding: 8px 12px;
      border-top: 1px solid #eee;
      background: #fafafa;
      flex-wrap: wrap;
    }
    .actions button {
      padding: 6px 12px;
      background: transparent;
      color: #1f1f1f;
      border: 1px solid #ddd;
      border-radius: 6px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
    }
    .actions button:hover:not(:disabled) {
      background: #f0f0f0;
    }
    .actions button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .composer {
      display: flex;
      gap: 8px;
      padding: 12px;
      border-top: 1px solid #eee;
    }
    .composer input {
      flex: 1;
      padding: 10px 12px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 14px;
      outline: none;
    }
    .composer input:focus {
      border-color: var(--sos-chat-accent, #1f1f1f);
    }
    /* SÓ o botão Enviar é escuro. Sem o [type="submit"], esta regra (mais
       específica que .attach-btn/.mic-btn) pintava os botões de ícone de preto. */
    .composer button[type="submit"] {
      padding: 10px 18px;
      background: var(--sos-chat-accent, #1f1f1f);
      color: var(--sos-chat-accent-fg, #fff);
      border: 0;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
    }
    .composer button[type="submit"]:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    /* Resumo: renderizado dentro de .messages como bolha inline (rola junto
       com o histórico, não bloqueia a tela). align-self pra não esticar
       width:100% via flex-stretch. */
    .summary {
      align-self: stretch;
      padding: 14px 16px;
      background: #f7f7f8;
      border-left: 3px solid var(--sos-chat-accent, #1f1f1f);
      border-radius: 4px;
      font-size: 13px;
    }
    .summary h3 {
      margin: 0 0 6px 0;
      font-size: 14px;
    }
    .summary ul {
      margin: 4px 0 8px 18px;
      padding: 0;
    }
    .summary .label {
      font-weight: 600;
      margin-top: 8px;
    }
    /* Resumo "ao vivo": Markdown em streaming antes do card estruturado final.
       Estilos mínimos pros elementos que o resumo usa (títulos, listas, prosa). */
    .summary.summary-live > *:first-child { margin-top: 0; }
    .summary.summary-live > *:last-child { margin-bottom: 0; }
    .summary h2, .summary h4 { font-weight: 700; line-height: 1.25; margin: 0.6em 0 0.2em; }
    .summary h2 { font-size: 14px; }
    .summary h4 { font-size: 13px; }
    .summary p { margin: 0.4em 0; }
    .summary li { margin: 0.15em 0; }
    /* Bullets do card estruturado passam por renderMarkdown (bloco) pra
       renderizar fórmulas; o <p> que ele insere não deve abrir espaço extra. */
    .summary li > p { margin: 0; }
    .summary li > p:last-child { margin-bottom: 0; }
    .summary .katex-display { margin: 0.3em 0; }
    .summary strong { font-weight: 600; }
    .summary em { font-style: italic; }
    .summary .summary-typing { color: #888; }
    /* Header: navegação entre conversas */
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 12px;
      border-bottom: 1px solid #eee;
      background: #fafafa;
      border-radius: 12px 12px 0 0;
    }
    .hbtn {
      padding: 5px 10px;
      background: transparent;
      color: #1f1f1f;
      border: 1px solid #ddd;
      border-radius: 6px;
      cursor: pointer;
      font-size: 12px;
      font-weight: 500;
    }
    .hbtn:hover:not(:disabled) {
      background: #f0f0f0;
    }
    .hbtn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    /* Grupo de ações à direita do header (Nova + crescer/popup + tela cheia) */
    .header-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    /* Botão só-ícone (quadrado) pros controles de popup/fullscreen */
    .hbtn-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
    }
    .hbtn-icon svg {
      width: 16px;
      height: 16px;
      display: block;
    }
    /* Botão "ligado" (modo ativo) — destaca o estado atual */
    .hbtn.active {
      background: #e8e8e8;
      border-color: #bbb;
    }
    /* Body: contêiner posicionado — ancora o botão flutuante e o painel */
    .body {
      position: relative;
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    /* Botão flutuante "rolar até o final" (estilo chat de IA) */
    .scroll-bottom {
      position: absolute;
      bottom: 12px;
      left: 50%;
      transform: translateX(-50%);
      width: 34px;
      height: 34px;
      padding: 0;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ddd;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1f1f1f;
      z-index: 5;
    }
    .scroll-bottom:hover {
      background: #f5f5f5;
    }
    .scroll-bottom svg {
      width: 18px;
      height: 18px;
      display: block;
    }
    /* Painel de conversas (overlay sobre as mensagens) */
    .sessions-panel {
      position: absolute;
      inset: 0;
      background: #fff;
      z-index: 10;
      overflow-y: auto;
      padding: 10px;
    }
    .sessions-panel .sp-info {
      padding: 24px 12px;
      color: #888;
      font-size: 13px;
      text-align: center;
    }
    .session-row {
      display: flex;
      align-items: stretch;
      gap: 4px;
      margin-bottom: 6px;
    }
    .session-item {
      display: flex;
      flex: 1;
      min-width: 0;
      flex-direction: column;
      gap: 2px;
      padding: 10px 12px;
      border: 1px solid #eee;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      text-align: left;
      font-family: inherit;
    }
    .si-del {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      border: 1px solid #eee;
      border-radius: 8px;
      background: #fff;
      color: #b71c1c;
      cursor: pointer;
    }
    .si-del:hover {
      background: #fdecea;
    }
    .si-del svg {
      width: 16px;
      height: 16px;
      display: block;
    }
    .si-act {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      border: 1px solid #eee;
      border-radius: 8px;
      background: #fff;
      color: #555;
      cursor: pointer;
    }
    .si-act:hover {
      background: #f0f0f0;
    }
    .si-act svg {
      width: 15px;
      height: 15px;
      display: block;
    }
    .sp-filter {
      width: 100%;
      box-sizing: border-box;
      padding: 8px 10px;
      margin-bottom: 8px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 13px;
      outline: none;
      font-family: inherit;
    }
    .sp-filter:focus {
      border-color: var(--sos-chat-accent, #1f1f1f);
    }
    .session-item:hover {
      background: #f5f5f5;
    }
    .session-item.active {
      border-color: var(--sos-chat-accent, #1f1f1f);
    }
    .session-item .si-title {
      font-size: 13px;
      font-weight: 600;
      color: #1f1f1f;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .session-item .si-meta {
      font-size: 11px;
      color: #888;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* ── Anexos ──────────────────────────────────────────────────────────── */
    /* Aviso de gate: staff abriu o chat do aluno (403 do backend). */
    .gate {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      gap: 8px;
      min-height: 260px;
      padding: 32px 22px;
    }
    .gate-ico {
      font-size: 34px;
    }
    .gate-title {
      font-size: 17px;
      font-weight: 600;
    }
    .gate-msg {
      font-size: 14px;
      color: var(--muted, #5f5e5a);
      max-width: 380px;
      line-height: 1.5;
    }
    /* Área de anexos staged (acima do composer) */
    .staged-area {
      padding: 8px 12px 0;
    }
    .attach-error {
      font-size: 12px;
      color: #b71c1c;
      margin-bottom: 6px;
    }
    .staged-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .staged-item {
      position: relative;
      display: flex;
      align-items: center;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: #fafafa;
      overflow: hidden;
    }
    .staged-item img {
      width: 56px;
      height: 56px;
      object-fit: cover;
      display: block;
    }
    .staged-doc {
      padding: 8px 10px;
      font-size: 12px;
      color: #555;
      max-width: 180px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .staged-remove {
      position: absolute;
      top: 2px;
      right: 2px;
      width: 18px;
      height: 18px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.6);
      color: #fff;
      font-size: 11px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .staged-remove:hover {
      background: rgba(0, 0, 0, 0.8);
    }
    /* Botão de clipe (anexar) no composer */
    .attach-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      padding: 0;
      background: transparent;
      border: 1px solid #ddd;
      border-radius: 8px;
      color: #555;
      cursor: pointer;
      flex: 0 0 auto;
    }
    .attach-btn:hover:not(:disabled) {
      background: #f0f0f0;
    }
    .attach-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .attach-btn svg {
      width: 18px;
      height: 18px;
      display: block;
    }
    /* Overlay de arrastar-e-soltar sobre o corpo do chat */
    .drop-overlay {
      position: absolute;
      inset: 0;
      z-index: 20;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(31, 31, 31, 0.06);
      border: 2px dashed var(--sos-chat-accent, #1f1f1f);
      border-radius: 12px;
      color: var(--sos-chat-accent, #1f1f1f);
      font-weight: 600;
      font-size: 15px;
      pointer-events: none;
    }
    /* Bolha do aluno com anexos (thumbnails das imagens / chips de documento) */
    .user-msg {
      align-self: flex-end;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
      max-width: 85%;
    }
    .msg-attachments {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      justify-content: flex-end;
    }
    .att-thumb {
      max-width: 180px;
      max-height: 180px;
      border-radius: 10px;
      border: 1px solid #eee;
      display: block;
    }
    .att-chip {
      display: inline-block;
      padding: 6px 10px;
      background: #f0f0f0;
      border-radius: 10px;
      font-size: 12px;
      color: #333;
      max-width: 220px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* ── Voz (microfone / gravação) ──────────────────────────────────────── */
    .mic-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      padding: 0;
      background: transparent;
      border: 1px solid #ddd;
      border-radius: 8px;
      color: #555;
      cursor: pointer;
      flex: 0 0 auto;
    }
    .mic-btn:hover:not(:disabled) {
      background: #f0f0f0;
    }
    .mic-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .mic-btn svg {
      width: 18px;
      height: 18px;
      display: block;
    }
    /* Barra de gravação (substitui o composer enquanto grava) */
    .rec-bar {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      border-top: 1px solid #eee;
    }
    .rec-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #e53935;
      flex: 0 0 auto;
      animation: rec-pulse 1s ease-in-out infinite;
    }
    @keyframes rec-pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.25;
      }
    }
    .rec-time {
      font-variant-numeric: tabular-nums;
      font-size: 13px;
      color: #1f1f1f;
      flex: 0 0 auto;
    }
    .rec-track {
      flex: 1;
      height: 6px;
      background: #eee;
      border-radius: 999px;
      overflow: hidden;
    }
    .rec-fill {
      height: 100%;
      background: var(--sos-chat-accent, #1f1f1f);
      border-radius: 999px;
      transition: width 0.25s linear;
    }
    .rec-fill.warn {
      background: #e53935;
    }
    .rec-remaining {
      font-variant-numeric: tabular-nums;
      font-size: 12px;
      color: #888;
      flex: 0 0 auto;
    }
    .rec-remaining.warn {
      color: #e53935;
      font-weight: 600;
    }
    .rec-btn {
      padding: 6px 12px;
      border-radius: 8px;
      border: 1px solid #ddd;
      background: transparent;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      flex: 0 0 auto;
    }
    .rec-cancel {
      color: #555;
    }
    .rec-cancel:hover {
      background: #f0f0f0;
    }
    .rec-ok {
      background: var(--sos-chat-accent, #1f1f1f);
      color: var(--sos-chat-accent-fg, #fff);
      border-color: var(--sos-chat-accent, #1f1f1f);
    }
    .rec-ok:hover {
      filter: brightness(0.85);
    }
  `,
  ];

  @property({ attribute: 'api-base' }) apiBase = '';
  @property({ attribute: 'token' }) token = '';
  @property({ attribute: 'session-id' }) sessionId = '';

  // Tela cheia via Fullscreen API nativa (não um atributo CSS): o navegador
  // promove o elemento ao "top layer", escapando qualquer ancestral com
  // transform/contain que prenderia um position:fixed na coluna do Moodle.
  @state() private isFullscreen = false;
  // Janela popup aberta (outra janela do navegador). Enquanto aberta, o widget
  // embutido fica bloqueado pelo overlay .window-lock — reabilita só ao fechar.
  @state() private popupOpen = false;
  private popupWindow: Window | null = null;
  // id do setInterval que vigia o fechamento da janela popup (0 = parado).
  private popupPoll = 0;

  @state() private messages: ChatMessage[] = [];
  @state() private input = '';
  @state() private streaming = false;
  // 'greeting' = streaming da apresentação (saudação) na abertura, antes de o
  // aluno digitar. Trava o composer enquanto roda (curto). Ver loadIntro.
  @state() private greeting = false;
  // Staff (gestor/professor) abriu o chat do aluno → backend responde 403. Em vez
  // de um chat quebrado, mostramos um aviso apontando pro Painel do Parceiro.
  @state() private gateBlocked = false;
  // Anexos escolhidos, ainda não enviados, + estados de UI de anexo.
  @state() private staged: StagedAttachment[] = [];
  @state() private dragOver = false;
  @state() private uploading = false;
  @state() private attachError: string | null = null;
  // Limite de tamanho do anexo (MB), vindo do /chat/intro (config do admin).
  @state() private attachmentMaxMb = 10;
  // Nome do tutor (placeholder + tooltips), vindo do /chat/intro (config do admin).
  @state() private tutorName = 'tutor';
  // Busca da lista de conversas (filtro client-side por título).
  @state() private sessionFilter = '';
  // Capabilities de anexo derivadas do claim `recursos` do JWT (memoizadas por
  // token). É só dica de UI — o servidor reforça o gating no upload.
  private _capsToken = '';
  private _capsCache: { image: boolean; doc: boolean; pdf: boolean; audio: boolean } = {
    image: false,
    doc: false,
    pdf: false,
    audio: false,
  };
  // Voz (microfone → transcrição). audioMaxSeconds vem do /chat/intro (config do
  // admin); 0 = entrada por voz desligada.
  @state() private recording = false;
  @state() private recordSeconds = 0;
  @state() private transcribing = false;
  @state() private audioMaxSeconds = 60;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private recordChunks: Blob[] = [];
  private recordTimer = 0;
  private recordCancelled = false;
  // `summary` = último resumo gerado; serve de referência pro botão "Exportar
  // PDF". O resumo também entra na timeline (messages) como item role:'summary'.
  @state() private summary: SummaryResponse | null = null;
  @state() private summarizing = false;
  @state() private exporting = false;
  @state() private exportingConversa = false;
  // Histórico de conversas do aluno (painel "Conversas")
  @state() private sessions: SessionMeta[] = [];
  @state() private showSessions = false;
  @state() private loadingSessions = false;
  @state() private loadingHistory = false;
  // Botão flutuante "rolar até o final": some quando já está no fim
  @state() private atBottom = true;
  // Balão de limite de uso: aparece acima da barra de ações quando o aluno
  // estoura o limite de tokens. Informa quando libera e dá pra fechar. Some
  // sozinho quando uma mensagem volta a passar (evento 'start').
  @state() private limitInfo: { message: string; blockedUntil: string | null } | null = null;
  @state() private limitDismissed = false;
  // TTS (🔊): id da mensagem cujo áudio está sendo gerado / tocando agora.
  // Só um áudio por vez — tocar outro (ou clicar de novo) para o anterior.
  @state() private ttsLoadingId: string | null = null;
  @state() private ttsPlayingId: string | null = null;

  private messagesEl: HTMLElement | null = null;
  private streamingBuffer = '';
  // rAF throttle: renderMarkdown (marked + KaTeX + DOMPurify) é caro pra rodar
  // a cada token. Sem throttle, uma resposta de 500 tokens vira O(n²) — cada
  // delta força re-parse da string inteira. Limitamos a 1 render por frame
  // (~16ms / 60fps); o efeito "typewriter" continua suave, mas o custo cai.
  private renderScheduled = false;
  private pendingContent: string | null = null;
  private pendingVariant: 'assistant' | 'blocked' = 'assistant';
  // Throttle separado pro resumo em streaming (mesma lógica de 1 render/frame,
  // mas alvo é o item role:'summary', não a bolha do assistente).
  private summaryBuffer = '';
  private summaryRenderScheduled = false;
  private pendingSummaryContent: string | null = null;
  // Elemento <audio> ativo do TTS (streaming) — fora do estado reativo.
  private ttsAudio: HTMLAudioElement | null = null;

  private onInput(e: Event) {
    this.input = (e.target as HTMLInputElement).value;
  }

  // ── Anexos (prints/documentos) ──────────────────────────────────────────────
  // Capabilities derivadas do claim `recursos` do JWT, memoizadas por token.
  private get caps(): { image: boolean; doc: boolean; pdf: boolean; audio: boolean } {
    if (this._capsToken !== this.token) {
      this._capsToken = this.token;
      this._capsCache = this.computeCaps();
    }
    return this._capsCache;
  }

  private computeCaps(): { image: boolean; doc: boolean; pdf: boolean; audio: boolean } {
    let recursos: string[] = [];
    try {
      const payload = this.token.split('.')[1];
      if (payload) {
        const json = decodeURIComponent(
          atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
            .split('')
            .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
            .join(''),
        );
        const claims = JSON.parse(json);
        if (Array.isArray(claims.recursos)) recursos = claims.recursos;
      }
    } catch {
      recursos = [];
    }
    const has = (k: string) => recursos.includes(k);
    // 'anexo' libera tudo; 'imagem'/'pdf' liberam só a categoria. Espelha
    // allowed_exts_for do backend.
    return {
      image: has('anexo') || has('imagem'),
      doc: has('anexo'),
      pdf: has('anexo') || has('pdf'),
      audio: has('audio'),
    };
  }

  private get canAttach(): boolean {
    const c = this.caps;
    return c.image || c.doc || c.pdf;
  }

  // Voz disponível: ligada globalmente pelo admin (limite > 0) e navegador com
  // MediaRecorder. SEM gate por recurso 'audio' do token — vale pra todos quando
  // o admin liga (audio_max_seconds > 0); 0 desliga pra todos.
  private get canRecord(): boolean {
    return this.audioMaxSeconds > 0 && typeof MediaRecorder !== 'undefined';
  }

  private get acceptAttr(): string {
    // Anexo liberado aceita todos os tipos suportados (imagem/print, txt/csv/md, pdf).
    return 'image/*,.txt,.csv,.md,.pdf,application/pdf';
  }

  private onAttachClick() {
    this.renderRoot.querySelector<HTMLInputElement>('.file-input')?.click();
  }

  private onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files) this.stageFiles(Array.from(input.files));
    input.value = ''; // permite re-selecionar o mesmo arquivo
  }

  private onPaste(e: ClipboardEvent) {
    if (!this.canAttach) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault(); // cola o arquivo, não o "caminho" como texto
      this.stageFiles(files);
    }
  }

  private onDragOver(e: DragEvent) {
    if (!this.canAttach) return;
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      this.dragOver = true;
    }
  }

  private onDragLeave() {
    this.dragOver = false;
  }

  private onDrop(e: DragEvent) {
    this.dragOver = false;
    if (!this.canAttach) return;
    const files = e.dataTransfer?.files;
    if (files && files.length) {
      e.preventDefault();
      this.stageFiles(Array.from(files));
    }
  }

  /** Valida e adiciona arquivos à área staged (tipo/tamanho/limite). */
  private stageFiles(files: File[]) {
    if (!this.canAttach) return;
    const maxMb = this.attachmentMaxMb;
    this.attachError = null;
    for (const file of files) {
      if (this.staged.length >= 5) {
        this.attachError = 'Máximo de 5 anexos por mensagem.';
        break;
      }
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const isImage =
        file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
      const isPdf = file.type === 'application/pdf' || ext === 'pdf';
      const isDoc =
        ['txt', 'csv', 'md'].includes(ext) ||
        ['text/plain', 'text/csv', 'text/markdown'].includes(file.type);
      // Com anexo habilitado, aceita qualquer tipo SUPORTADO (print/imagem,
      // txt/csv/md, pdf) — sem restrição por subtipo de recurso.
      let kind: 'image' | 'document' | null = null;
      if (isImage) kind = 'image';
      else if (isPdf || isDoc) kind = 'document';
      if (!kind) {
        this.attachError = `Tipo não suportado: ${file.name}`;
        continue;
      }
      if (file.size > maxMb * 1024 * 1024) {
        this.attachError = `${file.name} passa de ${maxMb} MB.`;
        continue;
      }
      const previewUrl = kind === 'image' ? URL.createObjectURL(file) : undefined;
      this.staged = [...this.staged, { localId: crypto.randomUUID(), file, kind, previewUrl }];
    }
  }

  private removeStaged(localId: string) {
    const a = this.staged.find((s) => s.localId === localId);
    if (a?.previewUrl) URL.revokeObjectURL(a.previewUrl);
    this.staged = this.staged.filter((s) => s.localId !== localId);
  }

  private clearStaged() {
    for (const s of this.staged) if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
    this.staged = [];
    this.attachError = null;
  }

  /** Sobe cada anexo staged (POST /attachments) e devolve os ids na ordem. */
  private async uploadStaged(items: StagedAttachment[], sessionId: string): Promise<string[]> {
    const ids: string[] = [];
    for (const s of items) {
      const fd = new FormData();
      fd.append('file', s.file);
      fd.append('session_id', sessionId);
      const r = await fetch(`${this.apiBase}/api/v1/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}` },
        body: fd,
      });
      if (!r.ok) {
        // Surface o motivo real do backend (ex.: "arquivo acima do limite de 10 MB",
        // "tipo .docx não é permitido") em vez de uma mensagem genérica.
        let detail = `Falha no envio (HTTP ${r.status}).`;
        try {
          const errBody = (await r.json()) as { detail?: unknown };
          if (errBody?.detail) detail = String(errBody.detail);
        } catch {
          /* resposta sem corpo JSON */
        }
        throw new Error(detail);
      }
      const data = (await r.json()) as { id: string };
      ids.push(data.id);
    }
    return ids;
  }

  // ── Voz (microfone → transcrição, estilo Claude) ────────────────────────────
  private fmtSec(s: number): string {
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  }

  private async startRecording() {
    if (
      this.recording ||
      this.transcribing ||
      this.streaming ||
      this.summarizing ||
      this.greeting ||
      this.uploading ||
      !this.canRecord
    )
      return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.attachError = 'Não foi possível acessar o microfone.';
      return;
    }
    let mr: MediaRecorder;
    try {
      mr = new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      this.attachError = 'Gravação não suportada neste navegador.';
      return;
    }
    this.mediaStream = stream;
    this.mediaRecorder = mr;
    this.recordChunks = [];
    this.recordCancelled = false;
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size) this.recordChunks.push(e.data);
    };
    mr.onstop = () => this.finishRecording();
    mr.start();
    this.recording = true;
    this.recordSeconds = 0;
    this.attachError = null;
    // Conta o tempo e auto-para no limite configurado pelo admin.
    this.recordTimer = window.setInterval(() => {
      this.recordSeconds += 1;
      if (this.recordSeconds >= this.audioMaxSeconds) this.stopRecording();
    }, 1000);
  }

  private stopRecording() {
    if (!this.recording) return;
    if (this.recordTimer) {
      clearInterval(this.recordTimer);
      this.recordTimer = 0;
    }
    this.recording = false;
    try {
      this.mediaRecorder?.stop(); // dispara onstop → finishRecording
    } catch {
      this.finishRecording();
    }
  }

  private cancelRecording() {
    this.recordCancelled = true;
    this.stopRecording();
  }

  private finishRecording() {
    this.stopMediaStream();
    const chunks = this.recordChunks;
    this.recordChunks = [];
    const mr = this.mediaRecorder;
    this.mediaRecorder = null;
    if (this.recordCancelled || chunks.length === 0) {
      this.recordCancelled = false;
      return;
    }
    const type = mr?.mimeType || 'audio/webm';
    void this.transcribeAudio(new Blob(chunks, { type }), this.recordSeconds);
  }

  private stopMediaStream() {
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
  }

  private async transcribeAudio(blob: Blob, seconds: number) {
    this.transcribing = true;
    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';
      const fd = new FormData();
      fd.append('file', blob, `fala.${ext}`);
      fd.append('duration_seconds', String(seconds));
      const r = await fetch(`${this.apiBase}/api/v1/transcribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.token}` },
        body: fd,
      });
      if (!r.ok) {
        this.attachError = 'Não foi possível transcrever o áudio.';
        return;
      }
      const data = (await r.json()) as { text: string };
      const text = (data.text || '').trim();
      if (!text) {
        this.attachError = 'Não captei nenhuma fala.';
        return;
      }
      // Dictation (estilo Claude): coloca no input pra o aluno revisar e enviar.
      this.input = this.input ? `${this.input} ${text}` : text;
      requestAnimationFrame(() =>
        this.renderRoot
          .querySelector<HTMLInputElement>('.composer input[type="text"]')
          ?.focus(),
      );
    } catch {
      this.attachError = 'Não foi possível transcrever o áudio.';
    } finally {
      this.transcribing = false;
    }
  }

  // Enter envia. Fazemos explicitamente (em vez de depender do submit implícito do
  // <form>) porque em TELA CHEIA — onde o host <sos-chat> é o elemento em fullscreen
  // — o submit implícito por Enter não dispara em alguns navegadores. preventDefault()
  // aqui cancela o submit nativo, então o onSubmit roda UMA vez só (sem envio duplo).
  private onInputKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      void this.onSubmit(e);
    }
  };

  private async onSubmit(e: Event) {
    e.preventDefault();
    const hasText = !!this.input.trim();
    if (
      (!hasText && this.staged.length === 0) ||
      this.streaming ||
      this.summarizing ||
      this.greeting ||
      this.uploading ||
      this.recording ||
      this.transcribing
    )
      return;
    const userText = this.input.trim();
    // Snapshot dos anexos staged e limpa a área de composição (NÃO revoga os
    // previewUrl: eles seguem vivos no balão enviado).
    const toSend = this.staged;
    this.staged = [];
    this.input = '';
    this.streaming = true;
    this.streamingBuffer = '';
    // Bolha do aluno (já com os thumbnails dos anexos) + bolha (vazia) do
    // assistente. Mesmo se bloqueado por limite, a timeline mantém o que ele
    // tentou enviar com a resposta logo abaixo.
    const msgAttachments = toSend.map((s) => ({
      name: s.file.name,
      kind: s.kind,
      previewUrl: s.previewUrl,
    }));
    this.messages = [
      ...this.messages,
      { role: 'user', content: userText, attachments: msgAttachments },
    ];
    this.messages = [...this.messages, { role: 'assistant', content: '' }];

    // Sessão nova: cria ANTES de subir anexo/streamar. 429 (limite) não cria
    // conversa e já preenche o balão de limite — não streamamos.
    if (!this.sessionId) {
      const ok = await this.createSession();
      if (!ok) {
        this.streaming = false;
        return;
      }
    }
    // Salvaguarda: nunca streamar/subir anexo sem session_id válido.
    if (!this.sessionId) {
      this.updateLastAssistant('Erro ao iniciar a conversa.', 'blocked');
      this.streaming = false;
      return;
    }

    // Sobe os anexos (precisam da sessão) e coleta os ids pro turno. Falha de
    // upload não streama — avisa e mantém a conversa utilizável.
    let attachmentIds: string[] = [];
    if (toSend.length) {
      this.uploading = true;
      try {
        attachmentIds = await this.uploadStaged(toSend, this.sessionId);
      } catch (e) {
        this.uploading = false;
        const reason =
          e instanceof Error && e.message
            ? `Não foi possível enviar o anexo: ${e.message}`
            : 'Não foi possível enviar o anexo. Tente novamente.';
        this.updateLastAssistant(reason, 'blocked');
        this.streaming = false;
        return;
      }
      this.uploading = false;
    }

    const client = new SseClient({
      url: `${this.apiBase}/api/v1/chat/stream`,
      token: this.token,
      body: { session_id: this.sessionId, content: userText, attachment_ids: attachmentIds },
    });

    // 'start' só chega quando a mensagem passou pela checagem de limite —
    // ou seja, NÃO está bloqueado. Aproveita pra limpar o balão de limite.
    client.on('start', () => {
      this.limitInfo = null;
    });
    client.on('token', ({ delta }) => {
      this.streamingBuffer += delta;
      this.scheduleAssistantUpdate(this.streamingBuffer, 'assistant');
    });
    client.on('blocked', ({ message }) => {
      this.flushAssistantUpdate();
      this.updateLastAssistant(message ?? 'Esse conteúdo não está permitido.', 'blocked');
    });
    // Limite de tokens atingido: mensagem vermelha na timeline + balão fixo
    // acima das ações. Reabre o balão (limitDismissed=false) mesmo que o aluno
    // tenha fechado antes — cada tentativa bloqueada reexibe o aviso.
    client.on('limit', ({ message, blocked_until }) => {
      this.flushAssistantUpdate();
      this.updateLastAssistant(message ?? 'Limite de uso atingido.', 'limit');
      this.limitInfo = { message: message ?? '', blockedUntil: blocked_until ?? null };
      this.limitDismissed = false;
    });
    client.on('error', ({ detail }) => {
      this.flushAssistantUpdate();
      this.updateLastAssistant(`Erro: ${detail}`, 'blocked');
    });
    client.on('done', ({ message_id }) => {
      this.flushAssistantUpdate();
      // Marca o id persistido na bolha — é o que habilita o botão 🔊.
      this.setLastAssistantId(message_id);
    });

    // finally garante que `streaming` volta a false mesmo em blocked/limit/error
    // (o pipeline não manda 'done' nesses casos) — senão o composer trava.
    try {
      await client.start();
    } finally {
      this.flushAssistantUpdate();
      this.streaming = false;
    }
  }

  /**
   * Cria a conversa no backend. Retorna true se criou (sessionId setado) e
   * pode streamar; false se NÃO deve streamar (bloqueio por limite ou erro),
   * já tendo preenchido o balão correspondente na timeline.
   */
  private async createSession(): Promise<boolean> {
    const r = await fetch(`${this.apiBase}/api/v1/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify({}),
    });
    // 429 = limite de tokens atingido. O backend NÃO criou conversa. Preenche o
    // balão de limite igual ao evento 'limit' do stream (mensagem + balão fixo)
    // e sinaliza pro onSubmit não streamar.
    if (r.status === 429) {
      const data = await r.json().catch(() => ({}));
      const detail = (data && data.detail) || {};
      const message = detail.message ?? 'Limite de uso atingido.';
      this.flushAssistantUpdate();
      this.updateLastAssistant(message, 'limit');
      this.limitInfo = { message, blockedUntil: detail.blocked_until ?? null };
      this.limitDismissed = false;
      return false;
    }
    if (!r.ok) {
      this.flushAssistantUpdate();
      this.updateLastAssistant('Erro ao iniciar a conversa.', 'blocked');
      return false;
    }
    const data = await r.json();
    this.sessionId = data.id;
    return true;
  }

  private updateLastAssistant(
    content: string,
    variant: 'assistant' | 'blocked' | 'limit' = 'assistant',
  ) {
    const msgs = [...this.messages];
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'assistant') {
      msgs[msgs.length - 1] = { ...last, content, variant };
    }
    this.messages = msgs;
  }

  /** Marca o id (persistido) na última bolha do assistente — habilita o 🔊. */
  private setLastAssistantId(id: string) {
    if (!id) return;
    const msgs = [...this.messages];
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'assistant') {
      msgs[msgs.length - 1] = { ...last, id };
      this.messages = msgs;
    }
  }

  /**
   * Toca (ou para) o áudio TTS de uma resposta. Clicar no que toca = parar;
   * tocar outra para a anterior. O backend sintetiza a mensagem pelo id
   * (server-side) e cacheia — re-ouvir não re-cobra.
   */
  private async playTts(messageId: string) {
    if (this.ttsPlayingId === messageId) {
      this.stopTts();
      return;
    }
    if (this.ttsLoadingId) return; // já há uma geração em andamento
    this.stopTts();
    if (!this.sessionId) return;
    this.ttsLoadingId = messageId;
    try {
      // 1) Pega um ticket curto (com o Bearer). O ownership é checado aqui.
      const r = await fetch(`${this.apiBase}/api/v1/tts/ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({ session_id: this.sessionId, message_id: messageId }),
      });
      if (!r.ok) {
        this.ttsLoadingId = null;
        alert(`Não foi possível gerar o áudio (${r.status}).`);
        return;
      }
      const { ticket } = (await r.json()) as { ticket: string };
      // 2) <audio> nativo streama o MP3 em realtime — toca conforme os chunks
      //    chegam (funciona em iOS/Android/desktop, sem header na URL).
      const url = `${this.apiBase}/api/v1/tts/stream?ticket=${encodeURIComponent(ticket)}`;
      const audio = new Audio(url);
      this.ttsAudio = audio;
      // 'playing' = começou a tocar (primeiro chunk) → sai do loading.
      audio.addEventListener('playing', () => {
        this.ttsLoadingId = null;
        this.ttsPlayingId = messageId;
      });
      audio.addEventListener('ended', () => this.stopTts());
      audio.addEventListener('error', () => {
        this.ttsLoadingId = null;
        this.stopTts();
      });
      void audio.play().catch(() => {
        this.ttsLoadingId = null;
        this.stopTts();
      });
    } catch {
      this.ttsLoadingId = null;
      this.stopTts();
    }
  }

  /** Para o áudio atual (aborta o stream em andamento) e limpa o estado. Nula a
   *  ref ANTES de mexer no elemento pra um 'error'/'emptied' reentrante virar no-op. */
  private stopTts() {
    const audio = this.ttsAudio;
    this.ttsAudio = null;
    this.ttsPlayingId = null;
    this.ttsLoadingId = null;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load(); // aborta o fetch do stream
    }
  }

  private renderTtsButton(id: string) {
    const loading = this.ttsLoadingId === id;
    const playing = this.ttsPlayingId === id;
    const label = loading ? 'Gerando áudio…' : playing ? 'Parar' : 'Ouvir';
    // Tooltip do estado "Ouvir" usa o nome do tutor; o texto visível segue compacto.
    const title = loading
      ? 'Gerando áudio…'
      : playing
        ? 'Parar'
        : `Ouvir o ${this.tutorName}`;
    return html`
      <button
        class="tts-btn"
        @click=${() => this.playTts(id)}
        ?disabled=${loading}
        title=${title}
        aria-label=${title}
      >
        ${playing
          ? html`<svg viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>`
          : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 5 6 9H2v6h4l5 4V5z" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7" />
              <path d="M19.5 5a9 9 0 0 1 0 14" />
            </svg>`}
        <span>${label}</span>
      </button>
    `;
  }

  /**
   * Agenda atualização da bolha do assistente pra próximo frame. Se já tem
   * uma agendada, só substitui o conteúdo (coalesce). Garante no máximo 1
   * render por frame, mesmo se a OpenAI mandar 100 tokens/s.
   */
  private scheduleAssistantUpdate(content: string, variant: 'assistant' | 'blocked') {
    this.pendingContent = content;
    this.pendingVariant = variant;
    if (this.renderScheduled) return;
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.renderScheduled = false;
      if (this.pendingContent != null) {
        this.updateLastAssistant(this.pendingContent, this.pendingVariant);
        this.pendingContent = null;
      }
    });
  }

  /** Força flush do conteúdo pendente — usado em done/blocked/error pra não
   * perder o último delta que ainda estava na fila do rAF. */
  private flushAssistantUpdate() {
    if (this.pendingContent != null) {
      this.updateLastAssistant(this.pendingContent, this.pendingVariant);
      this.pendingContent = null;
    }
    this.renderScheduled = false;
  }

  private async generateSummary() {
    if (!this.sessionId || this.summarizing || this.streaming) return;
    this.summarizing = true;
    this.summaryBuffer = '';
    // Ancora um item de resumo "ao vivo" na timeline (rola junto com o
    // histórico). Recebe o Markdown em streaming; no 'done' vira o card
    // estruturado final. Próximas mensagens aparecem ABAIXO do resumo.
    this.messages = [...this.messages, { role: 'summary', content: '', streaming: true }];

    const client = new SseClient({
      url: `${this.apiBase}/api/v1/sessions/${this.sessionId}/summary/stream`,
      token: this.token,
      body: {},
    });

    client.on('token', ({ delta }) => {
      this.summaryBuffer += delta;
      this.scheduleSummaryUpdate(this.summaryBuffer);
    });
    client.on('done', (data) => {
      this.flushSummaryUpdate();
      const summary = data as SummaryResponse;
      this.summary = summary;
      // Troca o Markdown ao vivo pelo card estruturado final.
      this.updateLastSummary(this.summaryBuffer, { summary, streaming: false });
    });
    client.on('error', ({ detail }) => {
      this.flushSummaryUpdate();
      this.updateLastSummary(`Erro ao gerar resumo: ${detail}`, { streaming: false });
    });

    try {
      await client.start();
    } finally {
      this.flushSummaryUpdate();
      this.summarizing = false;
    }
  }

  private updateLastSummary(
    content: string,
    opts?: { summary?: SummaryResponse; streaming?: boolean },
  ) {
    const msgs = [...this.messages];
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'summary') {
      msgs[msgs.length - 1] = {
        ...last,
        content,
        ...(opts?.summary !== undefined ? { summary: opts.summary } : {}),
        ...(opts?.streaming !== undefined ? { streaming: opts.streaming } : {}),
      };
      this.messages = msgs;
    }
  }

  /** Igual ao scheduleAssistantUpdate, mas pro item role:'summary'. */
  private scheduleSummaryUpdate(content: string) {
    this.pendingSummaryContent = content;
    if (this.summaryRenderScheduled) return;
    this.summaryRenderScheduled = true;
    requestAnimationFrame(() => {
      this.summaryRenderScheduled = false;
      if (this.pendingSummaryContent != null) {
        this.updateLastSummary(this.pendingSummaryContent);
        this.pendingSummaryContent = null;
      }
    });
  }

  private flushSummaryUpdate() {
    if (this.pendingSummaryContent != null) {
      this.updateLastSummary(this.pendingSummaryContent);
      this.pendingSummaryContent = null;
    }
    this.summaryRenderScheduled = false;
  }

  private async exportPdf() {
    if (!this.summary || this.exporting) return;
    this.exporting = true;
    try {
      const r = await fetch(
        `${this.apiBase}/api/v1/summaries/${this.summary.id}/pdf/download`,
        { headers: { Authorization: `Bearer ${this.token}` } },
      );
      if (!r.ok) {
        alert(`Erro ao baixar PDF: ${r.status}`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resumo-${this.summary.session_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      this.exporting = false;
    }
  }

  /** Baixa a CONVERSA inteira em PDF com a marca da escola (endpoint on-demand).
   *  Independe do resumo — basta haver uma conversa aberta com mensagens. */
  private async exportConversa() {
    if (!this.sessionId || this.exportingConversa) return;
    this.exportingConversa = true;
    try {
      const r = await fetch(`${this.apiBase}/api/v1/sessions/${this.sessionId}/pdf`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!r.ok) {
        alert(`Erro ao baixar a conversa: ${r.status}`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversa-${this.sessionId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      this.exportingConversa = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // O fullscreenchange dispara também quando o usuário sai com Esc/F11 — então
    // o estado isFullscreen sempre reflete a realidade, sem listener de teclado.
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    // Tema do chat: buscado no updated() quando token/api-base ficam disponíveis.
  }

  // Busca a aparência do chat (cores/fonte) da instituição do aluno e aplica como
  // CSS vars no host. Opcional: se falhar (rede/404), o chat fica no visual padrão.
  private async loadChatTheme(): Promise<void> {
    try {
      const r = await fetch(`${this.apiBase}/api/v1/chat/tema`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!r.ok) return;
      this.applyChatTheme(await r.json());
    } catch {
      /* tema é opcional — sem ele o chat usa o fallback (visual original) */
    }
  }

  private applyChatTheme(t: {
    bg?: string;
    text?: string;
    accent?: string;
    bubble?: string;
    font?: string;
    radius?: number;
  }): void {
    const s = this.style;
    if (t.bg) s.setProperty('--sos-chat-bg', t.bg);
    if (t.text) s.setProperty('--sos-chat-text', t.text);
    if (t.bubble) s.setProperty('--sos-chat-bubble', t.bubble);
    if (t.accent) {
      s.setProperty('--sos-chat-accent', t.accent);
      // Texto do botão/balão do aluno: branco sobre accent escuro, escuro sobre claro.
      s.setProperty('--sos-chat-accent-fg', this.contrastOn(t.accent));
    }
    if (t.font) s.setProperty('--sos-chat-font', CHAT_FONT_MAP[t.font] ?? CHAT_FONT_MAP.system);
    if (typeof t.radius === 'number') s.setProperty('--sos-chat-radius', `${t.radius}px`);
  }

  // Texto sobre o accent: escolhe branco OU escuro (#1f1f1f) pelo MAIOR contraste
  // WCAG — não um limiar simples de luminância (que erra em tons médios, ex. azul
  // #5b9df0 fica melhor com texto escuro).
  private contrastOn(hex: string): string {
    let s = hex.trim().replace(/^#/, '');
    if (s.length === 3)
      s = s
        .split('')
        .map((c) => c + c)
        .join('');
    if (!/^[0-9a-fA-F]{6}$/.test(s)) return '#fff';
    const n = parseInt(s, 16);
    const lin = (v: number) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const lum = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
    const contrast = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    // branco (L=1) vs escuro #1f1f1f (L≈0.0137): usa o de maior contraste.
    return contrast(lum, 1) >= contrast(lum, 0.0137) ? '#fff' : '#1f1f1f';
  }

  disconnectedCallback() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    if (this.popupPoll) {
      clearInterval(this.popupPoll);
      this.popupPoll = 0;
    }
    // Se o widget some da página (troca de tela no Moodle), fecha a janela órfã.
    this.popupWindow?.close();
    this.stopTts();
    // Encerra gravação em andamento (timer + microfone) ao desmontar.
    if (this.recordTimer) {
      clearInterval(this.recordTimer);
      this.recordTimer = 0;
    }
    try {
      this.mediaRecorder?.stop();
    } catch {
      /* já parado */
    }
    this.stopMediaStream();
    // Libera os objectURL dos anexos staged ainda não enviados.
    for (const s of this.staged) if (s.previewUrl) URL.revokeObjectURL(s.previewUrl);
    super.disconnectedCallback();
  }

  // True quando ESTE documento é a própria janela popup (window.name marcado no
  // window.open). Usado pra esconder os controles de tamanho lá dentro.
  private get isPopupWindow(): boolean {
    return window.name === 'sos-chat-popup';
  }

  // Alterna a tela cheia nativa. Precisa de gesto do usuário — o clique no botão
  // já é o gesto. Falhas (permissão/iframe sem allow) são silenciosas.
  private async toggleFullscreen() {
    try {
      if (document.fullscreenElement === this) {
        await document.exitFullscreen();
      } else {
        await this.requestFullscreen();
      }
    } catch {
      /* fullscreen pode ser bloqueado; não quebra o chat */
    }
  }

  private onFullscreenChange = () => {
    this.isFullscreen = document.fullscreenElement === this;
  };

  // Abre a conversa numa janela separada do navegador. A janela carrega o mesmo
  // bundle (import.meta.url) e monta um <sos-chat> próprio — registries de custom
  // element são por-janela, então não dá pra "mover" o nó; a janela recria. Token
  // e afins vão como ATRIBUTOS escritos no HTML (mesma origem), nunca na URL.
  private openPopup() {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.focus();
      return;
    }
    const w = window.open(
      '',
      'sos-chat-popup',
      'width=480,height=760,menubar=no,toolbar=no,location=no,status=no',
    );
    if (!w) return; // bloqueado pelo navegador (sem gesto / popup blocker)

    const esc = (s: string) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    w.document.open();
    w.document.write(
      `<!doctype html><html lang="pt-br"><head><meta charset="utf-8">` +
        `<meta name="viewport" content="width=device-width, initial-scale=1">` +
        `<title>OraEduca — Tutoria IA</title><style>` +
        `html,body{margin:0;height:100%;background:#fff}` +
        `sos-chat{display:flex;width:100%;height:100%;max-width:none;border-radius:0;box-shadow:none}` +
        `</style></head><body>` +
        `<sos-chat api-base="${esc(this.apiBase)}" token="${esc(this.token)}" session-id="${esc(this.sessionId)}"></sos-chat>` +
        `<script type="module" src="${esc(import.meta.url)}"><\/script>` +
        `</body></html>`,
    );
    w.document.close();

    this.popupWindow = w;
    this.popupOpen = true;
    // window.closed só vira true depois que a janela fecha; não há evento
    // confiável cross-origin-safe, então vigiamos por polling leve.
    this.popupPoll = window.setInterval(() => {
      if (!this.popupWindow || this.popupWindow.closed) this.closePopupCleanup();
    }, 600);
  }

  private focusPopup() {
    this.popupWindow?.focus();
  }

  private closePopup() {
    this.popupWindow?.close();
    this.closePopupCleanup();
  }

  // Reabilita o widget embutido e recarrega a conversa, refletindo o que foi
  // conversado na janela extra (mensagens persistem no backend).
  private closePopupCleanup() {
    if (this.popupPoll) {
      clearInterval(this.popupPoll);
      this.popupPoll = 0;
    }
    this.popupWindow = null;
    this.popupOpen = false;
    if (this.sessionId) void this.openSession(this.sessionId);
  }

  firstUpdated() {
    this.messagesEl = this.renderRoot.querySelector<HTMLElement>('.messages');
    this.messagesEl?.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.sessionId) {
      // Retomando uma conversa: carrega o histórico e só aquece a conexão (sem
      // saudação — a conversa já começou).
      void this.openSession(this.sessionId);
      void this.loadIntro(false);
    } else {
      // Conversa nova: aquece a conexão E streama a apresentação antes de o aluno
      // digitar. O backend só aquece/saúda se o aluno NÃO estiver bloqueado por
      // limite — "só abre a conexão se não estiver bloqueado".
      void this.loadIntro(true);
    }
  }

  /**
   * Abertura do componente: chama /chat/intro (SSE). Aquece a conexão server-side
   * (só se o aluno NÃO estiver bloqueado por limite) e, quando greet=true e a
   * conversa está vazia, streama a apresentação do Prompt Mestre palavra a palavra
   * — mesma cadência da conversa. best-effort: nunca quebra o chat. Aluno
   * bloqueado → o backend manda 'limit' e nada é aquecido/streamado.
   */
  private async loadIntro(greet: boolean) {
    if (!this.apiBase) return;
    // Só saúda numa conversa realmente vazia e ociosa (não saúda por cima de
    // histórico recém-carregado nem de um stream em andamento).
    const willGreet = greet && !this.sessionId && this.messages.length === 0 && !this.streaming;
    this.greeting = willGreet;
    this.streamingBuffer = '';
    let bubbleAdded = false;
    const client = new SseClient({
      url: `${this.apiBase}/api/v1/chat/intro`,
      token: this.token,
      body: { greet: willGreet },
    });
    // Config da abertura (ex.: limite de duração da fala pra gravação de voz).
    client.on('start', ({ audio_max_seconds, attachment_max_mb, tutor_name }) => {
      if (typeof audio_max_seconds === 'number') this.audioMaxSeconds = audio_max_seconds;
      if (typeof attachment_max_mb === 'number') this.attachmentMaxMb = attachment_max_mb;
      if (typeof tutor_name === 'string' && tutor_name) this.tutorName = tutor_name;
    });
    // Aluno bloqueado por limite: nada foi aquecido/saudado. Mostra o balão de
    // limite (igual ao fluxo de envio) pra ele saber por que o tutor não saudou.
    client.on('limit', ({ message, blocked_until }) => {
      this.limitInfo = { message: message ?? '', blockedUntil: blocked_until ?? null };
      this.limitDismissed = false;
    });
    client.on('token', ({ delta }) => {
      if (!bubbleAdded) {
        // Cria a bolha da saudação só no 1º token — sem apresentação cadastrada,
        // nenhuma bolha vazia aparece.
        this.messages = [...this.messages, { role: 'assistant', content: '' }];
        bubbleAdded = true;
      }
      this.streamingBuffer += delta;
      this.scheduleAssistantUpdate(this.streamingBuffer, 'assistant');
    });
    // Erro na saudação é silencioso (é só a abertura), EXCETO 403: aí o backend
    // recusou porque quem abriu é staff — mostramos o aviso do gate.
    client.on('error', ({ detail }) => {
      if (String(detail ?? '').includes('403')) this.gateBlocked = true;
    });
    try {
      await client.start();
    } finally {
      this.flushAssistantUpdate();
      this.greeting = false;
    }
  }

  // Token/api-base podem chegar como atributos DEPOIS do connectedCallback (o
  // Moodle seta via JS). Busca o tema do chat quando o token aparece pela 1ª vez.
  private _themeToken = '';
  updated(changed: Map<string, unknown>) {
    // Auto-follow: só "cola" no fim enquanto o aluno estiver no fim. Se ele
    // rolou pra cima pra reler, não arrastamos a tela — o streaming continua e
    // o botão flutuante aparece pra voltar ao fim.
    if (this.atBottom) this.scrollToBottom();
    if (
      (changed.has('token') || changed.has('apiBase')) &&
      this.token &&
      this.apiBase &&
      this._themeToken !== this.token
    ) {
      this._themeToken = this.token;
      void this.loadChatTheme();
    }
  }

  private onScroll = () => {
    const el = this.messagesEl;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance <= 48;
    if (atBottom !== this.atBottom) this.atBottom = atBottom;
  };

  private scrollToBottom(smooth = false) {
    const el = this.messagesEl;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }

  private jumpToBottom() {
    this.atBottom = true;
    this.scrollToBottom(true);
  }

  private async toggleSessions() {
    this.showSessions = !this.showSessions;
    if (this.showSessions) await this.loadSessions();
  }

  private async loadSessions() {
    this.loadingSessions = true;
    try {
      const r = await fetch(`${this.apiBase}/api/v1/sessions`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!r.ok) return;
      this.sessions = (await r.json()) as SessionMeta[];
    } finally {
      this.loadingSessions = false;
    }
  }

  /** Soft-delete pelo aluno: some da lista DELE (admin segue contando). */
  private async deleteSession(e: Event, id: string) {
    e.stopPropagation();
    if (!confirm('Excluir esta conversa? Ela some da sua lista.')) return;
    try {
      const r = await fetch(`${this.apiBase}/api/v1/sessions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!r.ok && r.status !== 204) {
        alert('Não foi possível excluir a conversa.');
        return;
      }
      this.sessions = this.sessions.filter((s) => s.id !== id);
      // Se excluiu a conversa aberta, começa uma nova.
      if (this.sessionId === id) this.newConversation();
    } catch {
      alert('Não foi possível excluir a conversa.');
    }
  }

  /** Renomeia uma conversa do aluno (PATCH /sessions/{id}). */
  private async renameSession(e: Event, s: SessionMeta) {
    e.stopPropagation();
    const novo = prompt('Renomear conversa:', s.titulo || 'Conversa');
    if (novo == null) return; // cancelou
    const titulo = novo.trim();
    if (!titulo || titulo === s.titulo) return;
    try {
      const r = await fetch(`${this.apiBase}/api/v1/sessions/${s.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({ titulo }),
      });
      if (!r.ok) {
        alert('Não foi possível renomear a conversa.');
        return;
      }
      this.sessions = this.sessions.map((x) => (x.id === s.id ? { ...x, titulo } : x));
    } catch {
      alert('Não foi possível renomear a conversa.');
    }
  }

  private async openSession(id: string) {
    if (
      this.streaming ||
      this.summarizing ||
      this.loadingHistory ||
      this.recording ||
      this.transcribing
    )
      return;
    this.loadingHistory = true;
    this.showSessions = false;
    try {
      const r = await fetch(`${this.apiBase}/api/v1/sessions/${id}/messages`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!r.ok) {
        alert(`Erro ao carregar conversa: ${r.status}`);
        return;
      }
      const rows = (await r.json()) as { id: string; role: string; content: string }[];
      this.stopTts();
      this.sessionId = id;
      this.summary = null;
      this.messages = rows
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ id: m.id, role: m.role as 'user' | 'assistant', content: m.content }));
      this.atBottom = true;
    } finally {
      this.loadingHistory = false;
    }
  }

  private newConversation() {
    if (
      this.streaming ||
      this.summarizing ||
      this.greeting ||
      this.uploading ||
      this.recording ||
      this.transcribing
    )
      return;
    this.stopTts();
    this.clearStaged();
    this.sessionId = '';
    this.messages = [];
    this.summary = null;
    this.showSessions = false;
    this.atBottom = true;
    this.limitInfo = null;
    // Conversa nova → saúda de novo (e reaquece a conexão se não bloqueado).
    void this.loadIntro(true);
  }

  private formatDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /** Data/hora de liberação do bloqueio — inclui o ano (dia/mês/ano hh:mm). */
  private formatUnlock(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Controles de tamanho: popup (abre outra janela) e tela cheia (Fullscreen
  // API). Na própria janela popup eles somem — não faz sentido aninhar. O botão
  // de popup fica "ativo" enquanto a janela extra está aberta.
  private renderExpandControls() {
    if (this.isPopupWindow) return '';
    const isFull = this.isFullscreen;
    return html`
      <button
        class="hbtn hbtn-icon ${this.popupOpen ? 'active' : ''}"
        @click=${this.openPopup}
        title=${this.popupOpen ? 'Conversa aberta em outra janela' : 'Abrir em outra janela'}
        aria-label=${this.popupOpen ? 'Conversa aberta em outra janela' : 'Abrir em outra janela'}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 3h7v7" />
          <path d="M21 3l-9 9" />
          <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
        </svg>
      </button>
      <button
        class="hbtn hbtn-icon ${isFull ? 'active' : ''}"
        @click=${this.toggleFullscreen}
        title=${isFull ? 'Sair da tela cheia' : 'Tela cheia'}
        aria-label=${isFull ? 'Sair da tela cheia' : 'Tela cheia'}
      >
        ${isFull
          ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 3v3a2 2 0 0 1-2 2H3" />
              <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
              <path d="M3 16h3a2 2 0 0 1 2 2v3" />
              <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
            </svg>`
          : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3" />
              <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
              <path d="M3 16v3a2 2 0 0 0 2 2h3" />
              <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>`}
      </button>
    `;
  }

  // Barra de gravação de voz (substitui o composer enquanto grava): tempo,
  // barra que preenche até o limite e aviso quando o tempo está acabando.
  private renderRecordingBar() {
    const max = this.audioMaxSeconds || 1;
    const pct = Math.min(100, (this.recordSeconds / max) * 100);
    const remaining = Math.max(0, this.audioMaxSeconds - this.recordSeconds);
    const warn = remaining <= 10;
    return html`
      <div class="rec-bar">
        <span class="rec-dot"></span>
        <span class="rec-time">${this.fmtSec(this.recordSeconds)}</span>
        <div class="rec-track">
          <div class="rec-fill ${warn ? 'warn' : ''}" style="width:${pct}%"></div>
        </div>
        <span class="rec-remaining ${warn ? 'warn' : ''}">-${remaining}s</span>
        <button class="rec-btn rec-cancel" @click=${this.cancelRecording} title="Cancelar">
          Cancelar
        </button>
        <button class="rec-btn rec-ok" @click=${this.stopRecording} title="Concluir e transcrever">
          OK
        </button>
      </div>
    `;
  }

  render() {
    if (this.gateBlocked) {
      return html`<div class="gate">
        <div class="gate-ico">🎓</div>
        <div class="gate-title">Este espaço é do aluno</div>
        <div class="gate-msg">
          Como gestor/professor, use o <b>Painel do Parceiro</b> para ver os
          indicadores e falar com o Conect. O tutor do aluno não se aplica ao seu
          perfil.
        </div>
      </div>`;
    }
    const canSummarize = !!this.sessionId && !this.streaming && this.messages.length >= 2;
    return html`
      <div class="header">
        <button class="hbtn" @click=${this.toggleSessions} title="Minhas conversas">
          ☰ Conversas
        </button>
        <div class="header-actions">
          <button
            class="hbtn"
            @click=${this.newConversation}
            ?disabled=${this.streaming || this.greeting}
            title=${`Iniciar novo diálogo com ${this.tutorName}`}
          >
            Nova Conversa
          </button>
          ${this.renderExpandControls()}
        </div>
      </div>

      <div
        class="body"
        @dragover=${this.onDragOver}
        @dragleave=${this.onDragLeave}
        @drop=${this.onDrop}
      >
        <div class="messages">
          ${this.messages.map((m) => this.renderItem(m))}
        </div>
        ${this.dragOver ? html`<div class="drop-overlay">Solte para anexar 📎</div>` : ''}

        ${!this.atBottom
          ? html`
              <button
                class="scroll-bottom"
                @click=${this.jumpToBottom}
                title="Rolar até o final"
                aria-label="Rolar até o final"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
              </button>
            `
          : ''}

        ${this.showSessions ? this.renderSessionsPanel() : ''}
      </div>

      ${this.limitInfo && !this.limitDismissed
        ? html`
            <div class="limit-banner" role="status">
              <div class="lb-text">
                <strong>Limite de uso atingido.</strong>
                ${this.limitInfo.blockedUntil
                  ? html`<span>
                      Será liberado em ${this.formatUnlock(this.limitInfo.blockedUntil)}.
                    </span>`
                  : html`<span>Tente novamente mais tarde.</span>`}
              </div>
              <button
                class="lb-close"
                @click=${() => {
                  this.limitDismissed = true;
                }}
                title="Fechar aviso"
                aria-label="Fechar aviso"
              >
                ✕
              </button>
            </div>
          `
        : ''}

      <div class="actions">
        <button
          @click=${this.generateSummary}
          ?disabled=${!canSummarize || this.summarizing}
          title="Gera um resumo do que foi estudado nesta conversa (temas, dúvidas, próximos passos)."
        >
          ${this.summarizing ? 'Gerando resumo…' : '✦ Gerar resumo'}
        </button>
        <button
          @click=${this.exportPdf}
          ?disabled=${!this.summary || this.exporting}
          title="Baixa o RESUMO em PDF. Gere o resumo primeiro."
        >
          ${this.exporting ? 'Exportando…' : '⬇ Exportar resumo'}
        </button>
        <button
          @click=${this.exportConversa}
          ?disabled=${!this.sessionId ||
          !this.messages.some((m) => m.role === 'user' || m.role === 'assistant') ||
          this.exportingConversa}
          title="Baixa a CONVERSA inteira em PDF, com a marca da escola."
        >
          ${this.exportingConversa ? 'Exportando…' : '⬇ Exportar conversa'}
        </button>
      </div>

      ${this.staged.length || this.attachError
        ? html`
            <div class="staged-area">
              ${this.attachError ? html`<div class="attach-error">${this.attachError}</div>` : ''}
              <div class="staged-list">
                ${this.staged.map(
                  (s) => html`
                    <div class="staged-item" title=${s.file.name}>
                      ${s.kind === 'image' && s.previewUrl
                        ? html`<img src=${s.previewUrl} alt=${s.file.name} />`
                        : html`<span class="staged-doc">📎 ${s.file.name}</span>`}
                      <button
                        class="staged-remove"
                        @click=${() => this.removeStaged(s.localId)}
                        title="Remover anexo"
                        aria-label="Remover anexo"
                      >
                        ✕
                      </button>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
        : ''}

      ${this.recording
        ? this.renderRecordingBar()
        : html`
            <form class="composer" @submit=${this.onSubmit}>
              <input
                type="text"
                placeholder=${this.transcribing
                  ? 'Transcrevendo…'
                  : this.uploading
                    ? 'Enviando anexo…'
                    : `Pergunte ao ${this.tutorName}...`}
                .value=${this.input}
                @input=${this.onInput}
                @keydown=${this.onInputKey}
                @paste=${this.onPaste}
                ?disabled=${this.streaming ||
                this.summarizing ||
                this.loadingHistory ||
                this.greeting ||
                this.uploading ||
                this.transcribing}
              />
              ${this.canAttach
                ? html`
                    <input
                      type="file"
                      class="file-input"
                      multiple
                      accept=${this.acceptAttr}
                      @change=${this.onFileChange}
                      hidden
                    />
                    <button
                      type="button"
                      class="attach-btn"
                      @click=${this.onAttachClick}
                      ?disabled=${this.streaming ||
                      this.summarizing ||
                      this.greeting ||
                      this.uploading ||
                      this.transcribing}
                      title="Anexar print ou documento"
                      aria-label="Anexar print ou documento"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                    </button>
                  `
                : ''}
              ${this.canRecord
                ? html`
                    <button
                      type="button"
                      class="mic-btn"
                      @click=${this.startRecording}
                      ?disabled=${this.streaming ||
                      this.summarizing ||
                      this.greeting ||
                      this.uploading ||
                      this.transcribing}
                      title=${`Falar ao ${this.tutorName} (gravar e transcrever)`}
                      aria-label=${`Falar ao ${this.tutorName} (gravar e transcrever)`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                        <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                        <line x1="12" y1="18" x2="12" y2="22" />
                      </svg>
                    </button>
                  `
                : ''}
              <button
                type="submit"
                ?disabled=${this.streaming ||
                this.summarizing ||
                this.loadingHistory ||
                this.greeting ||
                this.uploading ||
                this.transcribing ||
                (!this.input.trim() && this.staged.length === 0)}
              >
                Enviar
              </button>
            </form>
          `}

      ${this.popupOpen
        ? html`
            <div class="window-lock">
              <div class="wl-card">
                <p class="wl-msg">
                  A conversa está aberta em outra janela. Feche-a para continuar por aqui.
                </p>
                <div class="wl-actions">
                  <button class="hbtn" @click=${this.focusPopup}>Ir para a janela</button>
                  <button class="hbtn" @click=${this.closePopup}>Fechar janela</button>
                </div>
              </div>
            </div>
          `
        : ''}
    `;
  }

  private renderItem(m: ChatMessage) {
    if (m.role === 'summary') {
      if (m.summary) return this.renderSummary(m.summary);
      // Streaming ao vivo (ou mensagem de erro): renderiza o Markdown parcial.
      return html`<div class="summary summary-live">
        ${m.content
          ? unsafeHTML(renderMarkdown(m.content))
          : html`<span class="summary-typing">Gerando resumo…</span>`}
      </div>`;
    }
    const cls = m.variant ?? m.role;
    // assistant render: markdown + LaTeX via KaTeX; user/blocked: texto puro
    if (cls === 'assistant' && m.content) {
      return html`
        <div class="assistant-row">
          <div class="bubble assistant">${unsafeHTML(renderMarkdown(m.content))}</div>
          ${m.id ? this.renderTtsButton(m.id) : ''}
        </div>
      `;
    }
    if (m.role === 'user' && m.attachments?.length) {
      return html`
        <div class="user-msg">
          <div class="msg-attachments">
            ${m.attachments.map((a) =>
              a.kind === 'image' && a.previewUrl
                ? html`<img class="att-thumb" src=${a.previewUrl} alt=${a.name} title=${a.name} />`
                : html`<span class="att-chip" title=${a.name}>📎 ${a.name}</span>`,
            )}
          </div>
          ${m.content ? html`<div class="bubble user">${m.content}</div>` : ''}
        </div>
      `;
    }
    return html`<div class="bubble ${cls}">${m.content || '…'}</div>`;
  }

  private renderSummary(summary: SummaryResponse) {
    return html`
      <div class="summary">
        <h3>${unsafeHTML(renderMarkdownInline(summary.structured.tema))}</h3>
        ${this.renderSummarySection('Conceitos-chave', summary.structured.conceitos_chave)}
        ${this.renderSummarySection('Dúvidas resolvidas', summary.structured.duvidas_resolvidas)}
        ${this.renderSummarySection('Aprendizados', summary.structured.aprendizados)}
        ${this.renderSummarySection('Próximos passos', summary.structured.proximos_passos)}
      </div>
    `;
  }

  private renderSessionsPanel() {
    const q = this.sessionFilter.trim().toLowerCase();
    const filtered = q
      ? this.sessions.filter((s) => (s.titulo || 'Conversa').toLowerCase().includes(q))
      : this.sessions;
    return html`
      <div class="sessions-panel">
        ${this.sessions.length > 0
          ? html`<input
              class="sp-filter"
              type="search"
              placeholder="Filtrar conversas…"
              .value=${this.sessionFilter}
              @input=${(e: Event) => {
                this.sessionFilter = (e.target as HTMLInputElement).value;
              }}
            />`
          : ''}
        ${this.loadingSessions
          ? html`<div class="sp-info">Carregando…</div>`
          : this.sessions.length === 0
            ? html`<div class="sp-info">Nenhuma conversa ainda.</div>`
            : filtered.length === 0
              ? html`<div class="sp-info">Nenhuma conversa encontrada.</div>`
              : filtered.map(
                  (s) => html`
                    <div class="session-row">
                      <button
                        class="session-item ${s.id === this.sessionId ? 'active' : ''}"
                        @click=${() => this.openSession(s.id)}
                      >
                        <span class="si-title">${s.titulo || 'Conversa'}</span>
                        <span class="si-meta">
                          ${this.formatDate(s.created_at)}${s.componente ? ` · ${s.componente}` : ''}
                        </span>
                      </button>
                      <button
                        class="si-act"
                        @click=${(e: Event) => this.renameSession(e, s)}
                        title="Renomear conversa"
                        aria-label="Renomear conversa"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </button>
                      <button
                        class="si-del"
                        @click=${(e: Event) => this.deleteSession(e, s.id)}
                        title="Excluir esta conversa"
                        aria-label="Excluir esta conversa"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M3 6h18" />
                          <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        </svg>
                      </button>
                    </div>
                  `,
                )}
      </div>
    `;
  }

  private renderSummarySection(label: string, items: string[]) {
    if (!items?.length) return '';
    return html`
      <div class="label">${label}</div>
      <ul>
        ${items.map((i) => html`<li>${unsafeHTML(renderMarkdown(i))}</li>`)}
      </ul>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sos-chat': SosChat;
  }
}
